/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Widget useful link component
 *
 * @author Yakov Gushchin
 * @version 1.18.1
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';


import { LinkBlock, Skeleton } from '../../../../../components/common';
import { ELinkTarget } from '../../../../../components/common/ExternalLink/ExternalLink';
import { pushGTMAnalytic } from '../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import initTranslationCommon from '../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import ICommonTranslation from '../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { setLocalStorageDataAction } from '../../../../../modules/storage/storageActions';
import { ILocalStorage } from '../../../../../modules/storage/storageReducer';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../modules/storage/storageSelectors';
import { ELocalStorageKeys } from '../../../../../modules/storage/storageTypes';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { ExternalRoutePath, openExternalRoute, openRoute } from '../../../../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../../../../routes/constants/routesConstants';
import { stringFormat } from '../../../../../utils/stringFormat';
import { useCheckCustomRole } from '../../../../../utils/useCheckCustomRole';
import {
    makeSelectAuthoritiesIsAssistant,
} from '../../../../PageAuth/selectors/authSelectors';
import { WidgetSkeletonParagraphNormal } from '../constants/widgetSkeletonContants';
import { IWidgetSearchAccountsItem } from '../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { WidgetUsefulStyled } from './styled/WidgetUsefulStyled';


export interface IWidgetUsefulLinks {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetUsefulLinks = IWidgetUsefulLinks & IConnectedProps & IDispatchProps;

const WidgetUsefulLinks = (props: TWidgetUsefulLinks) => {

    const {
        isAssistant,
        selectedAccount,

        pushGtmAnalytic,
        setLocalStorage,
    } = props;

    const isWizardCollationReportEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardCollationReport),
    );

    const isCustomRoleDocsManage = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_DOCS_MANAGE);
    const isCustomRoleSubsBlock = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_SUBSCRIBER_BLOCKING);
    const isCustomRoleLimits = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_LIMITS_MANAGE);

    const translation = props.translation.widgets.usefulLinks;

    const sendGtmAnalytic = useCallback((linkName: string) => {
        pushGtmAnalytic({
            operation: GTMOperations.widgetUsefulLinks.clickLinkWidgetUsefulLinks(linkName),
            withAccountType: false,
            withSusrId: false,
            withUserRole: false,
        });
    }, [pushGtmAnalytic]);

    const onLinkToSubsClick = useCallback((subsBtnName: AccessComponentKeyEnum, linkName: string, external = false) => {
        setLocalStorage({
            key: ELocalStorageKeys.TABLE_BTN_ACTIVE,
            value: subsBtnName,
        });

        sendGtmAnalytic(linkName);

        external
            ? openExternalRoute(RoutePath.subscriberMobile, ELinkTarget.BLANK)
            : appHistoryPush(RoutePath.subscriberMobile);
    }, [sendGtmAnalytic, setLocalStorage]);

    const onLinkDocumentClick = useCallback((external = false) => {
        sendGtmAnalytic(translation.links.document);
        const url = RoutePath.billsAccount.replace(CLIENT_ID, selectedAccount.id.toString());
        external ? openExternalRoute(url, ELinkTarget.BLANK) : openRoute(url);
    }, [translation.links.document, sendGtmAnalytic, selectedAccount.id]);

    const onLinkReconciliationClick = useCallback((external = false) => {
        sendGtmAnalytic(translation.links.reconciliation);
        const url = isWizardCollationReportEnabled ?
            RoutePath.wizardCollationReportFromAccountId.replace(CLIENT_ID, selectedAccount.id.toString())
            : stringFormat(ExternalRoutePath.accountCollationId, selectedAccount.id.toString());
        openExternalRoute(url, external ? ELinkTarget.BLANK : ELinkTarget.SELF);
    }, [isWizardCollationReportEnabled, translation.links.reconciliation, sendGtmAnalytic, selectedAccount.id]);

    return (
        <WidgetUsefulStyled>
            <Skeleton
                count={isAssistant ? 2 : 4}
                isLoading={!translation.title}
                render={() => WidgetSkeletonParagraphNormal}
            >
                <div className="widget-useful__title">{translation.title}</div>
                <div className="widget-useful__link-container">
                    {isCustomRoleDocsManage && (
                        <>
                        <LinkBlock
                            className="widget-useful__link"
                            url={RoutePath.billsAccount.replace(CLIENT_ID, selectedAccount.id.toString())}
                            onClick={() => onLinkDocumentClick()}
                            onWheelClick={() => onLinkDocumentClick(true)}
                        >
                            {translation.links.document}
                        </LinkBlock>
                            <LinkBlock
                            className="widget-useful__link"
                            onClick={() => onLinkReconciliationClick()}
                            onWheelClick={() => onLinkReconciliationClick(true)}
                            >
                        {translation.links.reconciliation}
                            </LinkBlock>
                        </>)}
                    {
                        (!isAssistant || isCustomRoleSubsBlock) && <LinkBlock
                            className="widget-useful__link"
                            url={RoutePath.subscriberMobile}
                            onClick={() => onLinkToSubsClick(
                                AccessComponentKeyEnum.FooterButtonLock,
                                translation.links.block,
                            )}
                            onWheelClick={() => onLinkToSubsClick(
                                AccessComponentKeyEnum.FooterButtonLock,
                                translation.links.block,
                                true,
                            )}
                        >
                            {translation.links.block}
                        </LinkBlock>

                    }
                    {
                        (!isAssistant || isCustomRoleLimits) && <LinkBlock
                            className="widget-useful__link"
                            url={RoutePath.subscriberMobile}
                            onClick={() => onLinkToSubsClick(
                                AccessComponentKeyEnum.FooterButtonDcruleChange,
                                translation.links.limit,
                            )}
                            onWheelClick={() => onLinkToSubsClick(
                                AccessComponentKeyEnum.FooterButtonDcruleChange,
                                translation.links.limit,
                                true,
                            )}
                        >
                            {translation.links.limit}
                        </LinkBlock>
                    }
                </div>
            </Skeleton>

        </WidgetUsefulStyled>
    );
};

WidgetUsefulLinks.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

export interface IConnectedProps {
    isAssistant: boolean;
    selectedAccount: IWidgetSearchAccountsItem;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isAssistant: makeSelectAuthoritiesIsAssistant(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

export interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    setLocalStorage: (payload: ILocalStorage) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    setLocalStorage: payload => dispatch(setLocalStorageDataAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetUsefulLinks);
